import { useCallback, useMemo, useRef } from 'react'
import { useLiveClientState } from '@components/Live/LiveClientProvider'
import { isEqual } from 'lodash-es'
import { useUserStore } from 'store/userSettingsStore'
import { firstBy } from 'thenby'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    useConnectivity,
    useLinkedAndDelegatedWallets,
    useMultipleMembershipStatus,
    useMyUserId,
    useSpaceLastAccessed,
    useSpaceUnreads,
    useTownsContext,
} from 'use-towns-client'
import { isTownBanned } from 'utils'

const EMPTY_ARRAY: string[] = []

/**
 * Hook to get spaces with active live audio (stage or listeners)
 */
function useLiveSpaceIds(): Set<string> {
    const liveSpaceIds = useLiveClientState(
        useCallback((state) => {
            const liveIds = new Set<string>()
            for (const [spaceId, spaceSummary] of Object.entries(state.spaces)) {
                if (!spaceSummary?.channels) {
                    continue
                }
                // Check if any channel has active users (stage or listeners)
                for (const channelState of Object.values(spaceSummary.channels)) {
                    if (
                        channelState.stage.totalCount > 0 ||
                        channelState.listeners.totalCount > 0
                    ) {
                        liveIds.add(spaceId)
                        break
                    }
                }
            }
            return liveIds
        }, []),
    )
    return liveSpaceIds ?? new Set()
}

export const useOrderedSpaces = ({
    spaceIds,
    favoritedSpaces,
}: {
    spaceIds: string[]
    favoritedSpaces: string[]
}) => {
    const { spaceUnreads } = useSpaceUnreads()
    const liveSpaceIds = useLiveSpaceIds()

    const prevOrderedSpaces = useRef<string[]>([])

    const orderedSpaces = useMemo(() => {
        const result = [...spaceIds].sort(
            firstBy((s: string) => {
                const index = favoritedSpaces.indexOf(s)
                return index === -1 ? Number.MAX_SAFE_INTEGER : index
            }, 1)
                // Prioritize spaces with active live audio
                .thenBy((s: string) => (liveSpaceIds.has(s) ? 1 : 0), -1)
                .thenBy((s: string) => (spaceUnreads[s] ? 1 : 0), -1)
                .thenBy((s: string) => {
                    const prevIndex = prevOrderedSpaces.current.indexOf(s)
                    return prevIndex === -1 ? Number.MAX_SAFE_INTEGER : prevIndex
                }),
        )

        if (isEqual(result, prevOrderedSpaces.current)) {
            return prevOrderedSpaces.current
        }

        prevOrderedSpaces.current = result
        return result
    }, [favoritedSpaces, spaceUnreads, spaceIds, liveSpaceIds])

    return orderedSpaces
}

// non-expired spaces, sorted by last accessed
export function useFilteredSortedSpacesByLastAccessed() {
    const { loggedInWalletAddress } = useConnectivity()
    const { spaces: rawSpaces } = useTownsContext()
    const rawSpaceIds = useMemo(() => rawSpaces.map((s) => s.id), [rawSpaces])

    // We probably do not need to pass wallets with delegations here, being extra cautious.
    const { data: wallets } = useLinkedAndDelegatedWallets({ walletAddress: loggedInWalletAddress })
    const userId = useMyUserId()

    const { data: membershipStatuses } = useMultipleMembershipStatus({
        spaceIds: rawSpaceIds,
        userId: userId || '',
        wallets: wallets || [],
        batchSize: 5,
    })

    const filteredSpaces = useMemo(() => {
        return rawSpaces.filter((space) => {
            const address = addressFromSpaceId(space.id)

            if (!address) {
                return false
            }

            const membershipStatus = membershipStatuses.get(space.id)
            if (membershipStatus?.isExpired) {
                return false
            }

            if (isTownBanned(address)) {
                return false
            }

            return true
        })
    }, [rawSpaces, membershipStatuses])

    const favoritedSpaces = useUserStore(
        (s) => (userId && s.users[userId]?.favoriteSpaces) || EMPTY_ARRAY,
    )

    const spaceIdsRef = useRef<string[]>([])
    const spaceIds = useMemo(() => {
        const ids = filteredSpaces.map((s) => s.id)
        if (isEqual(ids, spaceIdsRef.current)) {
            return spaceIdsRef.current
        }
        spaceIdsRef.current = ids
        return ids
    }, [filteredSpaces])

    const orderedSpaces = useOrderedSpacesByLastAccessed({ spaceIds, favoritedSpaces })

    return { spaceIds: orderedSpaces }
}

export const useOrderedSpacesByLastAccessed = ({
    spaceIds,
    favoritedSpaces,
}: {
    spaceIds: string[]
    favoritedSpaces: string[]
}) => {
    const spaceLastAccessed = useSpaceLastAccessed()

    const prevOrderedSpaces = useRef<string[]>([])

    const orderedSpaces = useMemo(() => {
        const result = [...spaceIds].sort(
            firstBy((s: string) => {
                const index = favoritedSpaces.indexOf(s)
                return index === -1 ? Number.MAX_SAFE_INTEGER : index
            }, 1).thenBy((s: string) => spaceLastAccessed[s] ?? 0, -1),
        )

        if (isEqual(result, prevOrderedSpaces.current)) {
            return prevOrderedSpaces.current
        }

        prevOrderedSpaces.current = result
        return result
    }, [spaceIds, favoritedSpaces, spaceLastAccessed])

    return orderedSpaces
}
